import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientsAPI, policesAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import DeleteCascadeModal from '../shared/DeleteCascadeModal';

function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers||{}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
}

const ORANGE = '#F18A00';
const ProductIcon = ({ type, size = 19, color = ORANGE }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    {type === 'gf'
      ? <path d="M3 21l18 0M3 10l18 0M5 6l7 -3l7 3M4 10l0 11M20 10l0 11M8 14l0 3M12 14l0 3M16 14l0 3"/>
      : <path d="M11.46 20.846a12 12 0 0 1 -7.96 -14.846a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3a12 12 0 0 1 -.09 7.06M15 19l2 2l4 -4"/>}
  </svg>
);

const SS = {
  demande_ouverture: { bg:'#FEF3E2', color:'#9A5A00' },
  demande_refusee:   { bg:'#FCEBEB', color:'#A32D2D' },
  demande_pieces:    { bg:'#FEF3E2', color:'#9A5A00' },
  en_cours_etude:    { bg:'#EDE9FE', color:'#5B21B6' },
  devis:      { bg:'#DBEAFE', color:'#1D4ED8' },
  souscrit:   { bg:'#D1FAE5', color:'#065F46' },
  suspendu:   { bg:'#FEE2E2', color:'#991B1B' },
  resilie:    { bg:'#F1F5F9', color:'#475569' },
  renouvelee: { bg:'#EDE9FE', color:'#5B21B6' },
  sans_objet: { bg:'#F1F5F9', color:'#94A3B8' },
};

function fmt(n)     { return n ? new Intl.NumberFormat('fr-FR',{minimumFractionDigits:2}).format(n)+' €' : '—'; }
function fmtD(d)    { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }
function expiring(d){ if(!d) return false; const diff=(new Date(d)-new Date())/86400000; return diff<30&&diff>=0; }

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isCourtier, isClient, isIT, isReadOnly } = useAuth();
  // Le courtier ET le client passent par la demande d'ouverture (pas de création directe).
  const isRequester = isCourtier || isClient;

  const [client, setClient]       = useState(null);
  const [polices, setPolices]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [creating, setCreating]   = useState('');   // 'rc_pro' | 'gf' | ''
  const [confirm, setConfirm]     = useState(null);
  const [showGfRequestDone, setShowGfRequestDone] = useState(false);
  const [requestingGf, setRequestingGf] = useState(false);
  const [gfRequestError, setGfRequestError] = useState('');
  const [amendements, setAmendements] = useState({});
  const [expanded, setExpanded]   = useState(() => new Set());

  const toggleExpanded = (policeId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(policeId)) next.delete(policeId);
      else next.add(policeId);
      return next;
    });
  };

  const STATUTS = {
    demande_ouverture: t('status.demande_ouverture'),
    demande_refusee:   t('status.demande_refusee'),
    demande_pieces:    t('status.demande_pieces'),
    en_cours_etude:    t('status.en_cours_etude'),
    devis:      t('status.devis'),
    souscrit:   t('status.souscrit'),
    suspendu:   t('status.suspendu'),
    resilie:    t('status.resilie'),
    renouvelee: t('status.renouvelee'),
    sans_objet: t('status.sans_objet'),
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([clientsAPI.get(id), policesAPI.list({ client_id:id, include_renewed:true })]);
      setClient(c); setPolices(p);
      const map = {};
      for (const pol of p) {
        if (pol.statut==='souscrit'||pol.statut==='renouvelee'||pol.est_archive) {
          try {
            const a = await authFetch(`/api/polices/${pol.id}/amendements`).then(r=>r.json());
            if (a.length) map[pol.id] = a;
          } catch{}
        }
      }
      setAmendements(map);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  async function handleCreate(produit) {
    setCreating(produit);
    try {
      const police = await authFetch('/api/polices', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ client_id:id, produit }),
      }).then(r=>r.json());
      if (police.error) throw new Error(police.error);
      navigate(produit==='gf' ? `/gf/${police.id}` : `/polices/${police.id}`);
    } catch(e) { alert(e.message); setCreating(''); }
  }

  // Courtier : demander l'ouverture d'un dossier GF (ne crée pas une police
  // instructible ; crée une demande que l'équipe Arcus doit valider).
  async function handleRequestGf() {
    setRequestingGf(true); setGfRequestError('');
    try {
      const res = await authFetch('/api/polices/demande-ouverture-gf', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ client_id:id }),
      });
      const data = await res.json();
      if (!res.ok) { setGfRequestError(data.error || 'Erreur'); return; }
      setShowGfRequestDone(true);
      load(); // rafraîchir la liste (la demande apparaît "en attente")
    } catch(e) { setGfRequestError(e.message); }
    finally { setRequestingGf(false); }
  }

  async function handleDelete(policeId) {
    // Kept for backwards-compat — actual cascade delete handled by DeleteCascadeModal
    try { await policesAPI.delete(policeId); setConfirm(null); load(); }
    catch(e) { alert(e.message); setConfirm(null); }
  }

  if (loading) return <div style={{color:'#64748B',padding:24}}>{t('common.loading')}</div>;
  if (!client) return <div style={{color:'#DC2626',padding:24}}>{t('common.not_found')}</div>;

  const siege = client.adresses?.find(a=>a.est_siege)||client.adresses?.[0]||{};
  const isActif = client.statut==='actif';
  // Top-level : exclure les polices renouvelées (remplacées par leur successeur)
  const policesRC = polices.filter(p => (!p.produit || p.produit==='rc_pro') && p.statut !== 'renouvelee');
  const policesGF = polices.filter(p => p.produit==='gf' && p.statut !== 'renouvelee');

  // Map id → police (pour résoudre les périodes précédentes)
  const policeById = {};
  for (const p of polices) policeById[p.id] = p;
  // Map child id → predecessor (police renouvelée d'origine, accessible via police_source_id)
  const prevPeriods = {};
  for (const p of polices) {
    if (p.police_source_id && policeById[p.police_source_id]) {
      prevPeriods[p.id] = policeById[p.police_source_id];
    }
  }

  // ── Table de polices ────────────────────────────────────────
  function PoliciesTable({ items, produit }) {
    if (!items.length) return (
      <div style={{padding:'24px 18px',textAlign:'center',color:'#94A3B8',fontSize:13}}>
        {t('client_detail.no_policies')}
      </div>
    );
    return (
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
        <thead>
          <tr style={{background:'#F8FAFC',borderBottom:'1px solid #E2E8F0'}}>
            {[t('client_detail.col_piece'),t('client_detail.col_status'),
              t('client_detail.col_premium'),t('client_detail.col_effect'),
              t('client_detail.col_expiry'),t('client_detail.col_actions')].map(h=>(
              <th key={h} style={{padding:'9px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:'#64748B',letterSpacing:'.06em'}}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map(p => {
            const s  = SS[p.statut]||SS.devis;
            const exp = expiring(p.date_echeance);
            const route = produit==='gf' ? `/gf/${p.id}` : `/polices/${p.id}`;
            // Courtier + Client : une demande GF non validée n'est pas instructible (clic bloqué).
            const lockedForCourtier = isRequester && p.statut === 'demande_ouverture';
            const subAvenants = amendements[p.id] || [];
            const prevPolice  = prevPeriods[p.id];
            const hasSubItems = subAvenants.length > 0 || !!prevPolice;
            const isExpanded  = expanded.has(p.id);
            const subCount    = subAvenants.length + (prevPolice ? 1 : 0);
            return (
              <React.Fragment key={p.id}>
                <tr style={{borderBottom:'1px solid #F1F5F9',cursor:lockedForCourtier?'default':'pointer'}}
                  onClick={()=>{ if(!lockedForCourtier) navigate(route); }}
                  onMouseEnter={e=>{ if(!lockedForCourtier) e.currentTarget.style.background='#F8FAFC'; }}
                  onMouseLeave={e=>e.currentTarget.style.background=''}>
                  <td style={{padding:'11px 14px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      {hasSubItems ? (
                        <button onClick={e=>{e.stopPropagation();toggleExpanded(p.id);}}
                          aria-label={isExpanded ? t('client_detail.collapse') : t('client_detail.expand')}
                          title={isExpanded ? t('client_detail.collapse') : t('client_detail.expand')}
                          style={{background:'none',border:'none',cursor:'pointer',padding:2,display:'flex',alignItems:'center'}}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                            style={{transform:isExpanded?'rotate(90deg)':'none',transition:'transform .15s'}}>
                            <path d="M9 6l6 6l-6 6"/>
                          </svg>
                        </button>
                      ) : (
                        <span style={{display:'inline-block',width:18}}/>
                      )}
                      <div>
                        <div style={{fontWeight:600,display:'flex',alignItems:'center',gap:8}}>
                          {p.n_police||p.n_devis}
                          {hasSubItems && !isExpanded && (
                            <span style={{fontSize:10,padding:'1px 7px',borderRadius:99,background:'#F1F5F9',color:'#64748B',fontWeight:600}}>
                              +{subCount}
                            </span>
                          )}
                        </div>
                        {p.n_police&&<div style={{fontSize:11,color:'#94A3B8',marginTop:2}}>{p.n_devis}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={{padding:'11px 14px'}}>
                    <span style={{background:s.bg,color:s.color,padding:'3px 9px',borderRadius:99,fontSize:12,fontWeight:700}}>
                      {STATUTS[p.statut]||p.statut}
                    </span>
                  </td>
                  <td style={{padding:'11px 14px',fontWeight:600}}>{fmt(p.prime_ttc)}</td>
                  <td style={{padding:'11px 14px',color:'#475569'}}>{fmtD(p.date_effet)}</td>
                  <td style={{padding:'11px 14px',color:exp?'#D97706':'#475569',fontWeight:exp?600:400}}>
                    {fmtD(p.date_echeance)}{exp&&' ⚠'}
                  </td>
                  <td style={{padding:'11px 14px'}} onClick={e=>e.stopPropagation()}>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap',alignItems:'center'}}>
                      {lockedForCourtier ? (
                        <span style={{fontSize:11.5,color:'#92400E',fontStyle:'italic'}}>
                          {t('gf.awaiting_validation')}
                        </span>
                      ) : (
                        <button onClick={()=>navigate(route)}
                          style={{fontSize:12,padding:'4px 9px',borderRadius:5,cursor:'pointer',background:'#EFF6FF',color:'#1D4ED8',border:'none',fontWeight:600}}>
                          {t('common.open')}
                        </button>
                      )}
                      {p.statut==='souscrit'&&produit!=='gf'&&!isReadOnly&&(
                        <button onClick={()=>navigate(`/amendements/nouveau/${p.id}`)}
                          style={{fontSize:12,padding:'4px 9px',borderRadius:5,cursor:'pointer',background:'#FFFBEB',color:'#D97706',border:'1px solid #FDE68A',fontWeight:600}}>
                          {t('client_detail.new_amendment')}
                        </button>
                      )}
                      {isIT && (
                        <button onClick={()=>setConfirm({id:p.id,num:p.n_devis||p.n_police||'—',type:'police'})}
                          style={{fontSize:12,padding:'4px 9px',borderRadius:5,cursor:'pointer',background:'#FEE2E2',color:'#991B1B',border:'none',fontWeight:600}}
                          title={t('it_delete.title')}>
                          🗑
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Avenants */}
                {isExpanded && (amendements[p.id]||[]).map(av=>(
                  <tr key={av.id} style={{borderBottom:'1px solid #F1F5F9',background:'#FFFBEB',cursor:'pointer'}}
                    onClick={()=>navigate(`/amendements/${av.id}`)}>
                    <td style={{padding:'8px 14px 8px 42px',color:'#92400E',fontSize:12}}>
                      ↳ {av.n_amendement}
                    </td>
                    <td style={{padding:'8px 14px'}}>
                      <span style={{background:'#FEF3C7',color:'#92400E',padding:'2px 7px',borderRadius:99,fontSize:11,fontWeight:700}}>
                        {STATUTS[av.statut]||av.statut}
                      </span>
                    </td>
                    <td style={{padding:'8px 14px',fontSize:12,color:'#92400E'}}>{fmt(av.prime_ttc)}</td>
                    <td style={{padding:'8px 14px',fontSize:12,color:'#92400E'}}>{fmtD(av.date_effet)}</td>
                    <td colSpan={2}/>
                  </tr>
                ))}
                {/* Période précédente (si la police a une source via renouvellement) */}
                {isExpanded && prevPolice && (
                  <tr key={'prev-'+prevPolice.id} style={{borderBottom:'1px solid #F1F5F9',background:'#F5F3FF',cursor:'pointer'}}
                    onClick={()=>navigate(produit==='gf' ? `/gf/${prevPolice.id}` : `/polices/${prevPolice.id}`)}>
                    <td colSpan={4} style={{padding:'8px 14px 8px 42px',color:'#5B21B6',fontSize:12,display:'flex',alignItems:'center',gap:6}}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#5B21B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 11a8.1 8.1 0 0 0 -15.5 -2M4 5v4h4M4 13a8.1 8.1 0 0 0 15.5 2M20 19v-4h-4"/>
                      </svg>
                      ↳ {t('client_detail.previous_period')} : {prevPolice.n_devis}
                      <span style={{fontSize:11,color:'#7C3AED',fontWeight:600,marginLeft:6}}>
                        {fmtD(prevPolice.date_effet)} → {fmtD(prevPolice.date_echeance)}
                      </span>
                    </td>
                    <td style={{padding:'8px 14px'}}>
                      <span style={{background:'#EDE9FE',color:'#5B21B6',padding:'2px 7px',borderRadius:99,fontSize:11,fontWeight:700}}>
                        {STATUTS[prevPolice.statut]||prevPolice.statut}
                      </span>
                    </td>
                    <td/>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  }

  return (
    <div style={{margin:'0 auto'}}>
      {/* ── Header client ─────────────────────────────────────── */}
      <div style={{background:'#fff',borderRadius:10,border:'1px solid #E2E8F0',overflow:'hidden',marginBottom:18}}>
        <div style={{padding:'12px 18px',background:'#1A1A1A',display:'flex',alignItems:'center',gap:10}}>
          <button onClick={()=>navigate('/clients')}
            style={{background:'none',border:'none',cursor:'pointer',color:'#94A3B8',fontSize:18,lineHeight:1}}>←</button>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:'#64748B'}}>{t('clients.breadcrumb')}</div>
            <div style={{fontSize:17,fontWeight:700,color:'#fff'}}>{client.nom}</div>
          </div>
          <span style={{fontSize:11,padding:'3px 10px',borderRadius:99,fontWeight:700,
            background:isActif?'#D1FAE5':'#FEF3C7',color:isActif?'#065F46':'#92400E'}}>
            {isActif?t('status.client_actif'):t('status.prospect')}
          </span>
          {/* Modifier la fiche : réservé à Arcus (ni courtier, ni client, ni lecture seule) */}
          {!isRequester && !isReadOnly && (
            <button onClick={()=>navigate(`/clients/${id}`)}
              style={{fontSize:12,padding:'5px 12px',borderRadius:6,background:'rgba(255,255,255,.1)',color:'#fff',border:'1px solid rgba(255,255,255,.2)',cursor:'pointer'}}>
              {t('client_detail.modify_fiche')}
            </button>
          )}
          {isIT && (
            <button onClick={()=>setConfirm({id:client.id,num:client.nom,type:'client'})}
              title={t('it_delete.title')}
              style={{fontSize:12,padding:'5px 12px',borderRadius:6,background:'rgba(220,38,38,.25)',color:'#fff',border:'1px solid rgba(220,38,38,.5)',cursor:'pointer',display:'flex',alignItems:'center',gap:5}}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 7h16M10 11v6M14 11v6M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-12M9 7V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/>
              </svg>
              {t('common.delete')}
            </button>
          )}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:0,borderBottom:'1px solid #F1F5F9'}}>
          {[
            {l:t('client_detail.legal_status'),  v:client.statut_juridique||'—'},
            {l:t('client_detail.registration'),  v:client.n_immatriculation||'—'},
            {l:t('client_detail.market'),         v:client.marche_pays||'—'},
            {l:t('client_detail.distribution'),   v:client.type_relation==='direct'?t('client_detail.direct_distribution'):`${t('client_detail.brokered_distribution')} — ${client.intermediaire_nom||'—'}`},
          ].map((item,i)=>(
            <div key={i} style={{padding:'11px 16px',borderRight:i<3?'1px solid #F1F5F9':'none'}}>
              <div style={{fontSize:11,color:'#94A3B8',marginBottom:3}}>{item.l}</div>
              <div style={{fontSize:13,fontWeight:600,color:'#1E293B'}}>{item.v}</div>
            </div>
          ))}
        </div>
        {siege.adresse_1&&(
          <div style={{padding:'9px 16px',background:'#F8FAFC',fontSize:12,color:'#64748B'}}>
            📍 {[siege.adresse_1,siege.adresse_2,siege.code_postal,siege.ville,siege.pays].filter(Boolean).join(' · ')}
          </div>
        )}
      </div>

      {/* ── Section RC Pro ────────────────────────────────────── */}
      <div style={{background:'#fff',borderRadius:10,border:'1px solid #E2E8F0',overflow:'hidden',marginBottom:18}}>
        <div style={{padding:'13px 18px',borderBottom:'1px solid #F1F5F9',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontSize:15,fontWeight:700,display:'flex',alignItems:'center',gap:9}}>
              <ProductIcon type="rc_pro"/> {t('nav.polices')}
            </div>
            <div style={{fontSize:12,color:'#64748B',marginTop:2}}>
              {policesRC.length} {t(policesRC.length===1?'client_detail.contract_count':'client_detail.contract_count_plural')}
            </div>
          </div>
          {!isReadOnly && <button onClick={()=>handleCreate('rc_pro')} disabled={creating==='rc_pro'}
            style={{background:ORANGE,color:'#fff',border:'none',borderRadius:8,padding:'9px 16px',fontSize:13,cursor:'pointer',fontWeight:500,opacity:creating==='rc_pro'?.6:1}}>
            {creating==='rc_pro'?t('common.creating'):t('client_detail.create_policy')}
          </button>}
        </div>
        <PoliciesTable items={policesRC} produit="rc_pro"/>
      </div>

      {/* ── Section Garantie Financière ───────────────────────── */}
      <div style={{background:'#fff',borderRadius:10,border:'1px solid #E2E8F0',overflow:'hidden'}}>
        <div style={{padding:'13px 18px',borderBottom:'1px solid #F1F5F9',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div style={{fontSize:15,fontWeight:700,display:'flex',alignItems:'center',gap:9}}>
              <ProductIcon type="gf"/> {t('gf.title')}
            </div>
            <div style={{fontSize:12,color:'#64748B',marginTop:2}}>
              {policesGF.length} {t(policesGF.length===1?'client_detail.contract_count':'client_detail.contract_count_plural')}
            </div>
          </div>
          {/* Courtier + Client : bouton de demande d'ouverture (popup, pas de création directe) */}
          {isRequester && (
            <button onClick={handleRequestGf} disabled={requestingGf}
              style={{background:ORANGE,color:'#fff',border:'none',borderRadius:8,padding:'9px 16px',fontSize:13,cursor:requestingGf?'not-allowed':'pointer',fontWeight:500,opacity:requestingGf?.6:1}}>
              {requestingGf ? t('common.creating') : t('gf.request_open_btn')}
            </button>
          )}
          {/* Arcus : création directe d'une police GF */}
          {!isRequester && !isReadOnly && <button onClick={()=>handleCreate('gf')} disabled={creating==='gf'}
            style={{background:ORANGE,color:'#fff',border:'none',borderRadius:8,padding:'9px 16px',fontSize:13,cursor:'pointer',fontWeight:500,opacity:creating==='gf'?.6:1}}>
            {creating==='gf'?t('common.creating'):t('gf.create_policy')}
          </button>}
        </div>
        <PoliciesTable items={policesGF} produit="gf"/>
      </div>

      {/* Modal de suppression God Mode (IT uniquement) */}
      <DeleteCascadeModal
        open={!!confirm}
        onClose={()=>setConfirm(null)}
        onConfirmed={()=>{
          if (confirm?.type === 'client') navigate('/clients');
          else load();
        }}
        entityType={confirm?.type || 'police'}
        entityId={confirm?.id}
        entityLabel={confirm?.num}
      />

      {/* Popup : confirmation de demande d'ouverture GF (courtier) */}
      {showGfRequestDone && (
        <div onClick={()=>setShowGfRequestDone(false)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',
            alignItems:'center',justifyContent:'center',zIndex:1300}}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:'#fff',borderRadius:12,padding:'28px 26px',width:'min(460px, calc(100% - 32px))',textAlign:'center'}}>
            <div style={{width:48,height:48,borderRadius:'50%',background:'#FEF3E2',display:'flex',
              alignItems:'center',justifyContent:'center',margin:'0 auto 16px'}}>
              <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5l10 -10"/></svg>
            </div>
            <div style={{fontSize:14.5,color:'#1A1A1A',lineHeight:1.55,marginBottom:20}}>
              {t('gf.request_done_msg')}
            </div>
            <button onClick={()=>setShowGfRequestDone(false)}
              style={{background:ORANGE,color:'#fff',border:'none',borderRadius:8,padding:'10px 28px',fontSize:14,fontWeight:600,cursor:'pointer'}}>
              {t('common.ok') === 'common.ok' ? 'OK' : t('common.ok')}
            </button>
          </div>
        </div>
      )}

      {/* Erreur de demande GF (ex: doublon) */}
      {gfRequestError && (
        <div onClick={()=>setGfRequestError('')}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',
            alignItems:'center',justifyContent:'center',zIndex:1300}}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:'#fff',borderRadius:12,padding:'24px',width:'min(440px, calc(100% - 32px))'}}>
            <div style={{fontSize:14,color:'#991B1B',lineHeight:1.5,marginBottom:18}}>{gfRequestError}</div>
            <div style={{textAlign:'right'}}>
              <button onClick={()=>setGfRequestError('')}
                style={{background:'#1A1A1A',color:'#fff',border:'none',borderRadius:8,padding:'9px 22px',fontSize:14,fontWeight:600,cursor:'pointer'}}>
                {t('common.ok') === 'common.ok' ? 'OK' : t('common.ok')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
